import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import SfsCustomerApi from '@banking/api/services/sfs-customer-api';
import SfsExternalBankAccountApi from '@banking/api/services/sfs-external-bank-account-api';
import { ExternalBankAccount } from '@square/dashboard-protos/models/squareup/sfs/external/models/external_bank_account';
import { ImportableExternalBankAccount } from '@square/dashboard-protos/models/squareup/sfs/external/models/importable_external_bank_account';
import Sentry from '@banking/ui/services/sentry';
import PlaidState from '@banking/ui/services/plaid-state';
import { PlaidLink } from '@square/dashboard-protos/models/squareup/sfs/external/models/plaid_link';
import type RouterService from '@ember/routing/router-service';

export interface Model {
  externalBankAccounts: readonly ExternalBankAccount[];
  importableExternalBankAccounts: readonly ImportableExternalBankAccount[];
  plaidLink?: PlaidLink;
}

export default class LinkExternalBankAccount extends Route {
  @service declare sfsExternalBankAccountApi: SfsExternalBankAccountApi;
  @service declare sfsCustomerApi: SfsCustomerApi;
  @service declare sentry: Sentry;
  @service declare plaidState: PlaidState;
  @service declare router: RouterService;

  async model(): Promise<Model> {
    const currentCustomer = this.sfsCustomerApi.currentCustomer;
    if (!currentCustomer) {
      throw new Error('cannot load settings with no customer');
    }
    const { id } = currentCustomer;

    let externalBankAccounts: readonly ExternalBankAccount[] = [];
    let importableExternalBankAccounts: readonly ImportableExternalBankAccount[] =
      [];
    let plaidLink;

    try {
      externalBankAccounts =
        await this.sfsExternalBankAccountApi.listExternalBankAccounts(id);
    } catch (error) {
      this.sentry.captureException(error);
    }

    try {
      importableExternalBankAccounts =
        await this.sfsExternalBankAccountApi.listImportableExternalBankAccounts(
          id
        );
    } catch (error) {
      this.sentry.captureException(error);
    }

    try {
      plaidLink = await this.sfsExternalBankAccountApi.fetchPlaidLink(id);
    } catch (error) {
      this.sentry.captureException(error);
    }

    return {
      externalBankAccounts,
      importableExternalBankAccounts,
      plaidLink,
    };
  }

  redirect(model: Model) {
    if (model.plaidLink || model.importableExternalBankAccounts.length === 0) {
      this.router.transitionTo(
        'signup.link-external-bank-account.link-with-plaid'
      );
    } else {
      this.router.transitionTo(
        'signup.link-external-bank-account.import-bank-account'
      );
    }
  }
}
